import { SwapVert } from '@mui/icons-material'
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { TemplatesSortOrder } from './templatesSortOrder'

interface OwnProps {
  getSortedTemplates: (
    isClear: boolean,
    sortColumn: string,
    order: string,
  ) => void
  templatesSortOrder?: TemplatesSortOrder
}

const SortColumn = {
  TEMPLATE_NAME: 'template_name',
  CREATED_AT: 'created_at',
  UPDATED_AT: 'updated_at',
  EMPTY: '',
} as const

const SortOrder = {
  ASC: 'asc',
  DESC: 'desc',
} as const

type SortOrderValue = (typeof SortOrder)[keyof typeof SortOrder]

type SortDisplayValue = {
  name: string
  asc: string
  desc: string
}
type SortColumnKey = Exclude<(typeof SortColumn)[keyof typeof SortColumn], ''>
type SortDisplayObject = { [K in SortColumnKey]: SortDisplayValue }

const isSortColumnKey = (key: string): key is SortColumnKey =>
  key === SortColumn.TEMPLATE_NAME ||
  key === SortColumn.CREATED_AT ||
  key === SortColumn.UPDATED_AT

const sortDisplay: SortDisplayObject = {
  template_name: {
    name: 'ひな形名',
    asc: '五十音順',
    desc: '五十音 逆順',
  },
  created_at: {
    name: '作成日時',
    asc: '古い順',
    desc: '新しい順',
  },
  updated_at: {
    name: '更新日時',
    asc: '古い順',
    desc: '新しい順',
  },
}

const getDefaultOrder = (column: string): SortOrderValue =>
  column === SortColumn.TEMPLATE_NAME ? SortOrder.ASC : SortOrder.DESC

const TemplatesSortDropDown: React.FC<OwnProps> = (props: OwnProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null

  const sortColumn = props.templatesSortOrder?.sortColumn ?? SortColumn.EMPTY
  const order =
    props.templatesSortOrder?.sortOrder ?? getDefaultOrder(sortColumn)
  const [isClear, setIsClear] = useState(sortColumn === SortColumn.EMPTY)

  const changeSortColumn = (val: string) => {
    setIsClear(false)
    props.getSortedTemplates(false, val, getDefaultOrder(val))
  }

  const clearFilter = () => {
    setIsClear(true)
    props.getSortedTemplates(true, SortColumn.EMPTY, SortOrder.ASC)
  }

  const changeSortOrder = (e: SortOrderValue) => {
    setIsClear(false)
    props.getSortedTemplates(false, sortColumn, e)
  }

  const currentDisplay = isSortColumnKey(sortColumn)
    ? sortDisplay[sortColumn]
    : undefined
  const buttonLabel =
    !isClear && currentDisplay
      ? `並び替え: ${currentDisplay.name}(${order === SortOrder.ASC ? currentDisplay.asc : currentDisplay.desc})`
      : '並び替え'

  const hoverArea = (
    <Stack sx={{ p: 2 }} spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={clearFilter}>クリア</Button>
      </Box>
      <Select
        fullWidth
        size="small"
        displayEmpty
        value={sortColumn === SortColumn.EMPTY ? '' : sortColumn}
        onChange={(e: SelectChangeEvent<string>) =>
          changeSortColumn(e.target.value)
        }
        renderValue={val =>
          val ? (
            sortDisplay[val]?.name
          ) : (
            <Typography variant="body2" color="text.disabled">
              列名を選択
            </Typography>
          )
        }
      >
        {Object.keys(sortDisplay).map((key: string) => (
          <MenuItem key={key} value={key}>
            {sortDisplay[key]?.name}
          </MenuItem>
        ))}
      </Select>
      {!isClear && (
        <RadioGroup
          value={order}
          onChange={e => changeSortOrder(e.target.value as SortOrderValue)}
          sx={{ mt: 1.5 }}
        >
          {sortColumn === SortColumn.TEMPLATE_NAME ? (
            <>
              <FormControlLabel
                value={SortOrder.ASC}
                control={<Radio size="small" />}
                label={sortDisplay[sortColumn]?.asc}
              />
              <FormControlLabel
                value={SortOrder.DESC}
                control={<Radio size="small" />}
                label={sortDisplay[sortColumn]?.desc}
              />
            </>
          ) : (
            <>
              <FormControlLabel
                value={SortOrder.DESC}
                control={<Radio size="small" />}
                label={sortDisplay[sortColumn]?.desc}
              />
              <FormControlLabel
                value={SortOrder.ASC}
                control={<Radio size="small" />}
                label={sortDisplay[sortColumn]?.asc}
              />
            </>
          )}
        </RadioGroup>
      )}
    </Stack>
  )

  return (
    <Box>
      <Button
        color="secondary"
        variant="outlined"
        startIcon={<SwapVert />}
        onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
      >
        {buttonLabel}
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { width: '100%', maxWidth: 232 },
          },
        }}
      >
        {hoverArea}
      </Popover>
    </Box>
  )
}

export default TemplatesSortDropDown
